/**
 * /tools/dragon-trigger-log?clientId=X — TRK-053 (Should-priority).
 *
 * Per-trigger log paired w/ STP-036 reframings. Each entry: dateLabel,
 * context, dragonName, responseUsed, intensity 1..10, outcome (held /
 * partial / slipped). Persists as versioned `client_documents`
 * doc_type=`dragon_trigger_log`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type Outcome = 'held' | 'partial' | 'slipped' | '';

interface EntryRow {
  dateLabel: string;
  context: string;
  dragonName: string;
  responseUsed: string;
  intensity: string;
  outcome: Outcome;
}

interface FormState {
  entries: EntryRow[];
  reflection: string;
}

const EMPTY: EntryRow = {
  dateLabel: '',
  context: '',
  dragonName: '',
  responseUsed: '',
  intensity: '',
  outcome: '',
};

const DEFAULT_FORM: FormState = {
  entries: [{ ...EMPTY }],
  reflection: '',
};

export function DragonTriggerLogPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'dragon-trigger-log');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (Array.isArray(p.entries) && p.entries.length > 0) {
            next.entries = p.entries.map((raw) => {
              const e = raw as Record<string, unknown>;
              return {
                dateLabel: typeof e.dateLabel === 'string' ? e.dateLabel : '',
                context: typeof e.context === 'string' ? e.context : '',
                dragonName: typeof e.dragonName === 'string' ? e.dragonName : '',
                responseUsed: typeof e.responseUsed === 'string' ? e.responseUsed : '',
                intensity: typeof e.intensity === 'number' ? String(e.intensity) : '',
                outcome:
                  e.outcome === 'held' || e.outcome === 'partial' || e.outcome === 'slipped'
                    ? e.outcome
                    : '',
              };
            });
          }
          if (typeof p.reflection === 'string') next.reflection = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit =
    !submitting && hydrated && form.entries.some((e) => e.context.trim().length > 0);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.dragonTriggerLog.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      entries: form.entries
        .filter((e) => e.context.trim().length > 0)
        .map((e) => {
          const intensity = e.intensity.trim() ? Number(e.intensity) : NaN;
          return {
            ...(e.dateLabel.trim() ? { dateLabel: e.dateLabel.trim() } : {}),
            context: e.context.trim(),
            ...(e.dragonName.trim() ? { dragonName: e.dragonName.trim() } : {}),
            ...(e.responseUsed.trim() ? { responseUsed: e.responseUsed.trim() } : {}),
            ...(Number.isInteger(intensity) && intensity >= 1 && intensity <= 10
              ? { intensity }
              : {}),
            ...(e.outcome ? { outcome: e.outcome } : {}),
          };
        }),
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'dragon-trigger-log',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.dragonTriggerLog.errors.generic'));
      } else {
        setSubmitError(t('tools.dragonTriggerLog.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="dragon-trigger-log-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.dragonTriggerLog.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.dragonTriggerLog.errors.missingClient')}</p>
      </section>
    );
  }

  const setEntry = <K extends keyof EntryRow>(idx: number, key: K, value: EntryRow[K]): void => {
    setForm((prev) => ({
      ...prev,
      entries: prev.entries.map((e, i) => (i === idx ? { ...e, [key]: value } : e)),
    }));
  };
  const addEntry = (): void => {
    setForm((prev) =>
      prev.entries.length >= 50 ? prev : { ...prev, entries: [...prev.entries, { ...EMPTY }] },
    );
  };
  const removeEntry = (idx: number): void => {
    setForm((prev) =>
      prev.entries.length <= 1
        ? prev
        : { ...prev, entries: prev.entries.filter((_, i) => i !== idx) },
    );
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.dragonTriggerLog.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.dragonTriggerLog.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.dragonTriggerLog.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.dragonTriggerLog.entries.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.dragonTriggerLog.entries.hint')}</p>
          <ol className="mt-s3 space-y-s4">
            {form.entries.map((entry, idx) => (
              <li
                key={idx}
                className="rounded-md border border-bone-3 bg-bone p-s3 space-y-s2"
                data-testid={`dragon-entry-${idx}`}
              >
                <div className="grid grid-cols-12 gap-s2">
                  <input
                    type="text"
                    value={entry.dateLabel}
                    onChange={(e) => setEntry(idx, 'dateLabel', e.target.value)}
                    placeholder={t('tools.dragonTriggerLog.placeholders.dateLabel')}
                    className="input-field col-span-3"
                    data-testid={`dragon-date-${idx}`}
                  />
                  <input
                    type="text"
                    value={entry.dragonName}
                    onChange={(e) => setEntry(idx, 'dragonName', e.target.value)}
                    placeholder={t('tools.dragonTriggerLog.placeholders.dragonName')}
                    className="input-field col-span-4"
                    data-testid={`dragon-name-${idx}`}
                  />
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={entry.intensity}
                    onChange={(e) => setEntry(idx, 'intensity', e.target.value)}
                    placeholder="1-10"
                    className="input-field col-span-2"
                    data-testid={`dragon-intensity-${idx}`}
                    aria-label={t('tools.dragonTriggerLog.fields.intensity')}
                  />
                  <select
                    value={entry.outcome}
                    onChange={(e) => setEntry(idx, 'outcome', e.target.value as Outcome)}
                    className="input-field col-span-2"
                    data-testid={`dragon-outcome-${idx}`}
                    aria-label={t('tools.dragonTriggerLog.fields.outcome')}
                  >
                    <option value="">—</option>
                    <option value="held">{t('tools.dragonTriggerLog.outcome.held')}</option>
                    <option value="partial">{t('tools.dragonTriggerLog.outcome.partial')}</option>
                    <option value="slipped">{t('tools.dragonTriggerLog.outcome.slipped')}</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    disabled={form.entries.length <= 1}
                    className="col-span-1 btn-ghost text-sm"
                    data-testid={`dragon-remove-${idx}`}
                  >
                    ×
                  </button>
                </div>
                <textarea
                  value={entry.context}
                  onChange={(e) => setEntry(idx, 'context', e.target.value)}
                  placeholder={t('tools.dragonTriggerLog.placeholders.context')}
                  className="input-field min-h-16 w-full"
                  data-testid={`dragon-context-${idx}`}
                  rows={2}
                />
                <textarea
                  value={entry.responseUsed}
                  onChange={(e) => setEntry(idx, 'responseUsed', e.target.value)}
                  placeholder={t('tools.dragonTriggerLog.placeholders.response')}
                  className="input-field min-h-16 w-full"
                  data-testid={`dragon-response-${idx}`}
                  rows={2}
                />
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={addEntry}
            disabled={form.entries.length >= 50}
            className="btn-ghost mt-s3"
            data-testid="dragon-add"
          >
            + {t('tools.dragonTriggerLog.entries.add')}
          </button>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.dragonTriggerLog.fields.reflection')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.dragonTriggerLog.optional')}
              </span>
            </span>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-24"
              data-testid="dragon-reflection"
              rows={4}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="dragon-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="dragon-saved-at">
            ✓ {t('tools.dragonTriggerLog.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.dragonTriggerLog.submitting') : t('tools.dragonTriggerLog.submit')}
        </button>
      </form>
    </section>
  );
}
